// Ledningerne: let buede, farvet efter belastning (grøn, gul, orange). Rød kun når de er knækket.
// Tegnes igen hver frame i skærmkoordinater, så stregerne er skarpe ved enhver zoom.

import { Container, Graphics, Sprite } from 'pixi.js';
import type { Line } from '../sim';
import { bez, bezTan, dashSegments } from './geo';
import type { P } from './geo';
import { C, loadColor, mix } from './palette';
import type { LineGeo, View } from './view';

export function samples(l: LineGeo, n: number, tMax = 1): P[] {
  const out: P[] = [];
  for (let i = 0; i <= n; i++) out.push(bez(l.a, l.c, l.b, (i / n) * tMax));
  return out;
}

function path(g: Graphics, pts: readonly P[], dx = 0, dy = 0): void {
  const p0 = pts[0];
  if (!p0) return;
  g.moveTo(p0.x + dx, p0.y + dy);
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i] as P;
    g.lineTo(p.x + dx, p.y + dy);
  }
}

/** Stiplet polylinje: streg `on` px, mellemrum `off` px, forskudt med `shift`. */
function dashed(g: Graphics, pts: readonly P[], on: number, off: number, shift = 0): void {
  for (const [a, b] of dashSegments(pts, on, off, shift)) g.moveTo(a.x, a.y).lineTo(b.x, b.y);
}

export function lineWidth(l: Line, k: number): number {
  return (l.tier === 1 ? 3.6 : l.tier === 2 ? 5.6 : 7.8) * k;
}

export class GridLayer {
  readonly pylons = new Container();
  readonly g = new Graphics();
  readonly preview = new Graphics();
  /** Ledninger trukket i pause på Orkan: stiplede, til uret går igen (IGN-løkke 2). */
  readonly planned = new Graphics();
  private pool: Sprite[] = [];

  update(v: View): void {
    const g = this.g;
    g.clear();
    const k = v.k;
    const t = v.now / 1000;
    let used = 0;
    // Ledninger i en storms spor, der endnu ikke er forstærket: de får et lille skjold som opfordring.
    const shield = new Set<string>();
    for (const th of v.state.threats) if (th.kind === 'storm' && th.phase === 'warning') for (const id of th.atRisk) shield.add(id);
    // Skygger først, så ingen ledning kaster skygge på en anden.
    for (const l of v.state.lines) {
      const geo = v.lines.get(l.id);
      if (!geo) continue;
      const pts = samples(geo, 18, v.drawIn.get(l.id) ?? 1);
      path(g, pts, 0, 3 * k);
      g.stroke({ width: lineWidth(l, k) + 3, color: C.ink, alpha: 0.13, cap: 'round', join: 'round' });
    }
    for (const l of v.state.lines) {
      const geo = v.lines.get(l.id);
      if (!geo) continue;
      const tMax = v.drawIn.get(l.id) ?? 1;
      const pts = samples(geo, 18, tMax);
      const w = lineWidth(l, k);
      const broken = l.status === 'broken';
      // Pyloner langs luftledninger.
      if (l.medium === 'air' && tMax >= 1) {
        const n = Math.floor(geo.len / (46 * k));
        for (let i = 1; i < n; i++) {
          const p = bez(geo.a, geo.c, geo.b, i / n);
          let s = this.pool[used];
          if (!s) {
            s = v.atlas.sprite('pylon');
            this.pool.push(s);
            this.pylons.addChild(s);
          }
          s.visible = true;
          s.position.set(p.x, p.y + 6 * k);
          s.scale.set(k * 0.85);
          s.alpha = broken ? 0.45 : 0.9;
          used++;
        }
      }
      if (v.atRisk.has(l.id) && !broken) {
        path(g, pts);
        g.stroke({ width: w + 14 * k, color: C.orange, alpha: v.calm ? 0.35 : 0.25 + 0.2 * Math.sin(t * 6), cap: 'round', join: 'round' });
      }
      if (l.reinforced) {
        path(g, pts);
        g.stroke({ width: w + 8 * k, color: C.petrol, alpha: 0.35, cap: 'round', join: 'round' });
      }
      const sea = l.medium === 'sea' || l.medium === 'hvdc';
      path(g, pts);
      g.stroke({ width: w + 3.5 * k, color: sea ? mix(C.turquoise, C.white, 0.35) : C.white, cap: 'round', join: 'round' });
      if (broken) {
        dashed(g, pts, 7 * k, 6 * k);
        g.stroke({ width: w * 0.9, color: C.red, cap: 'round' });
        this.zigzag(g, geo, w, k);
        continue;
      }
      const idle = Math.abs(l.flow) < 1;
      const core = l.halfDown ? C.orange : idle ? mix(C.green, C.white, 0.45) : loadColor(l.loadPm);
      if (l.medium === 'air' && !l.halfDown) {
        path(g, pts);
      } else {
        dashed(g, pts, (l.medium === 'cable' ? 9 : 12) * k, 5 * k);
      }
      g.stroke({ width: w, color: core, cap: 'round', join: 'round' });
      if (l.tier === 3) {
        path(g, pts);
        g.stroke({ width: Math.max(1, w * 0.2), color: C.white, alpha: 0.9, cap: 'round', join: 'round' });
      }
      if (tMax >= 1) this.pips(g, geo, l, k);
      if (tMax >= 1 && v.state.coop && l.owner !== 'level') this.owner(g, geo, l.owner, k);
      if (shield.has(l.id) && !l.reinforced && tMax >= 1) this.shield(g, geo, k, t, v.calm);
    }
    for (let i = used; i < this.pool.length; i++) (this.pool[i] as Sprite).visible = false;
  }

  /** Kapacitet vises som 1 til 3 prikker midt på ledningen, ikke som MW. */
  private pips(g: Graphics, geo: LineGeo, l: Line, k: number): void {
    const m = bez(geo.a, geo.c, geo.b, 0.5);
    const tan = bezTan(geo.a, geo.c, geo.b, 0.5);
    const tl = Math.hypot(tan.x, tan.y) || 1;
    const ux = tan.x / tl;
    const uy = tan.y / tl;
    const gap = 7.5 * k;
    for (let i = 0; i < l.tier; i++) {
      const o = (i - (l.tier - 1) / 2) * gap;
      g.circle(m.x + ux * o, m.y + uy * o, 3.1 * k).fill(C.white).stroke({ width: 1.5 * k, color: C.ink });
    }
  }

  /**
   * Hvem byggede ledningen (co-op): et lille sædemærke tæt ved den ene ende. p1 er en grøn cirkel,
   * p2 en petrol firkant, så form og farve siger det samme (ikke kun farve).
   */
  private owner(g: Graphics, geo: LineGeo, who: 'p1' | 'p2', k: number): void {
    const m = bez(geo.a, geo.c, geo.b, geo.len > 140 * k ? 0.24 : 0.3);
    const r = 5.6 * k;
    if (who === 'p1') {
      g.circle(m.x, m.y + 1.5 * k, r + 2 * k).fill({ color: C.ink, alpha: 0.18 });
      g.circle(m.x, m.y, r + 2 * k).fill(C.white);
      g.circle(m.x, m.y, r).fill(C.green);
    } else {
      const s = r * 0.92;
      g.roundRect(m.x - s - 2 * k, m.y - s - 2 * k + 1.5 * k, (s + 2 * k) * 2, (s + 2 * k) * 2, 2.5 * k).fill({ color: C.ink, alpha: 0.18 });
      g.roundRect(m.x - s - 2 * k, m.y - s - 2 * k, (s + 2 * k) * 2, (s + 2 * k) * 2, 2.5 * k).fill(C.white);
      g.roundRect(m.x - s, m.y - s, s * 2, s * 2, 1.5 * k).fill(C.petrol);
    }
  }

  /** Skjold-mærke lidt over ledningens midte: "forstærk mig". */
  private shield(g: Graphics, geo: LineGeo, k: number, t: number, calm: boolean): void {
    const m = bez(geo.a, geo.c, geo.b, 0.5);
    const tan = bezTan(geo.a, geo.c, geo.b, 0.5);
    const tl = Math.hypot(tan.x, tan.y) || 1;
    let nx = -tan.y / tl;
    let ny = tan.x / tl;
    if (ny > 0) {
      nx = -nx;
      ny = -ny;
    }
    const r = 11 * k * (calm ? 1 : 1 + 0.06 * Math.sin(t * 5));
    const cx = m.x + nx * 20 * k;
    const cy = m.y + ny * 20 * k;
    g.circle(cx, cy + 2 * k, r).fill({ color: C.ink, alpha: 0.15 });
    g.circle(cx, cy, r).fill(C.white).stroke({ width: 2.5 * k, color: C.orange });
    const s = r * 0.62;
    g.moveTo(cx, cy - s)
      .lineTo(cx + s * 0.85, cy - s * 0.62)
      .lineTo(cx + s * 0.78, cy + s * 0.1)
      .quadraticCurveTo(cx + s * 0.55, cy + s * 0.75, cx, cy + s)
      .quadraticCurveTo(cx - s * 0.55, cy + s * 0.75, cx - s * 0.78, cy + s * 0.1)
      .lineTo(cx - s * 0.85, cy - s * 0.62)
      .closePath()
      .fill(C.petrol);
  }

  private zigzag(g: Graphics, geo: LineGeo, w: number, k: number): void {
    const m = bez(geo.a, geo.c, geo.b, 0.5);
    const tan = bezTan(geo.a, geo.c, geo.b, 0.5);
    const tl = Math.hypot(tan.x, tan.y) || 1;
    const ux = tan.x / tl;
    const uy = tan.y / tl;
    const nx = -uy;
    const ny = ux;
    const s = 6 * k + w * 0.5;
    const pts: P[] = [
      { x: m.x - ux * s * 1.2 + nx * s, y: m.y - uy * s * 1.2 + ny * s },
      { x: m.x - ux * s * 0.2 - nx * s * 0.2, y: m.y - uy * s * 0.2 - ny * s * 0.2 },
      { x: m.x + ux * s * 0.2 + nx * s * 0.4, y: m.y + uy * s * 0.2 + ny * s * 0.4 },
      { x: m.x + ux * s * 1.2 - nx * s, y: m.y + uy * s * 1.2 - ny * s },
    ];
    g.circle(m.x, m.y, s * 1.5).fill(C.white).stroke({ width: 2 * k, color: C.red });
    path(g, pts);
    g.stroke({ width: 2.6 * k, color: C.red, cap: 'round', join: 'round' });
  }

  /** Forhåndsvisning af en ledning, mens man trækker. */
  drawPreview(from: P | null, to: P | null, ok: boolean, snapped: boolean, k: number, now: number): void {
    const g = this.preview;
    g.clear();
    if (!from || !to) return;
    const pts = [from, to];
    // Grå, indtil fingeren er over en node: så kan man se, at et slip her ikke bygger noget.
    const color = !snapped ? C.cloud : ok ? C.green : C.orange;
    path(g, pts);
    g.stroke({ width: 9 * k, color: C.white, alpha: 0.85, cap: 'round' });
    dashed(g, pts, 10 * k, 7 * k, (now / 20) % 17);
    g.stroke({ width: 5 * k, color, cap: 'round' });
    if (snapped) {
      g.circle(to.x, to.y, 26 * k).stroke({ width: 4 * k, color: C.white });
      g.circle(to.x, to.y, 26 * k).stroke({ width: 2.4 * k, color });
    } else {
      g.circle(to.x, to.y, 7 * k).fill(color).stroke({ width: 2.5 * k, color: C.white });
    }
  }

  /** Planlagte ledninger: hvid stiplet streg med en lille urskive midt på. */
  drawPlanned(pairs: readonly [P, P][], k: number): void {
    const g = this.planned;
    g.clear();
    for (const [a, b] of pairs) {
      const pts = [a, b];
      path(g, pts);
      g.stroke({ width: 8 * k, color: C.white, alpha: 0.7, cap: 'round' });
      dashed(g, pts, 8 * k, 8 * k, 0);
      g.stroke({ width: 4 * k, color: C.cloud, cap: 'round' });
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      g.circle(m.x, m.y, 9 * k).fill(C.white).stroke({ width: 2 * k, color: C.cloud });
      g.moveTo(m.x, m.y).lineTo(m.x, m.y - 5 * k).moveTo(m.x, m.y).lineTo(m.x + 4 * k, m.y);
      g.stroke({ width: 1.6 * k, color: C.cloud, cap: 'round' });
    }
  }
}
